import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import CrearNuevoProdct from "./AdminCreateProduct";
import EditarProductoAdmin from "./EditarProducto";
import ProductSelector from "./ProductSelector";
import { useAdminSession } from "./hooks/useAdminSession";
import { useProductosAdmin } from "./hooks/useProductosAdmin";
import { useEdicionProducto } from "./hooks/useEdicionProducto";
import { useFotosPendientes } from "./hooks/useFotosPendientes";
import * as adminService from "../../services/admin.service";

const FORM_NUEVO_PRODUCTO = { nombre: "", precio: "", stock: "", imagen: "", descripcion: "" };

function Dashboard() {
  const navigate = useNavigate();

  // sesion del admin: token y cierre por inactividad
  const { cerrarSesion } = useAdminSession(navigate);

  // lista de productos y sus acciones de alta, baja y modificacion
  const { productos, crearProducto, actualizarProducto, borrarProducto } = useProductosAdmin(cerrarSesion);

  // producto que se esta editando en el panel de abajo
  const edicion = useEdicionProducto(cerrarSesion);

  // fotos elegidas y sus previsualizaciones, para crear o editar
  const fotos = useFotosPendientes(edicion.idProductoEditado);

  const [nuevoProducto, setNuevoProducto] = useState(FORM_NUEVO_PRODUCTO);
  const [creando, setCreando] = useState(false);

  const inputClass = "bg-transparent border border-border text-ink text-xs px-4 py-3.5 focus:outline-none focus:border-[#111111] transition-all placeholder-[#999999] w-full font-sans rounded";

  // sube las fotos pendientes para un producto puntual, si es que hay
  async function subirFotosPendientes(productoId) {
    if (!fotos.fotosSeleccionadas) return;

    const formData = new FormData();
    Array.from(fotos.fotosSeleccionadas).forEach((archivo) => formData.append("imagenes", archivo));

    try {
      await adminService.subirImagenes(productoId, formData);
    } catch (error) {
      toast.error("error al subir las imagenes");
    }

    fotos.limpiar();
  }

  async function manejarCrearProducto() {
    if (creando) return;
    if (!nuevoProducto.nombre || !nuevoProducto.precio || !nuevoProducto.stock) {
      toast.error("debes completar nombre, precio y stock");
      return;
    }

    setCreando(true);
    try {
      const producto = await crearProducto({
        nombre: nuevoProducto.nombre.trim(),
        precio: Number(nuevoProducto.precio),
        stock: Number(nuevoProducto.stock),
        descripcion: nuevoProducto.descripcion,
      });

      if (producto) {
        setNuevoProducto(FORM_NUEVO_PRODUCTO);
        await subirFotosPendientes(producto.id);
        toast.success("producto creado");
      }
    } catch (error) {
      const mensaje = error.response?.data?.error || error.response?.data?.mensaje || "error al crear el producto";
      toast.error(mensaje);
    } finally {
      setCreando(false);
    }
  }

  async function manejarSeleccionarProducto(id) {
    fotos.setFotosSeleccionadas(null);
    await edicion.abrirPanel(id, productos);
  }

  async function manejarActualizarProducto(id) {
    try {
      const soloRellenos = Object.fromEntries(
        Object.entries(edicion.actualizarDatos).filter(([clave, valor]) => clave === "descripcion" || valor !== "")
      );
      await actualizarProducto(id, soloRellenos);
      await subirFotosPendientes(id);
      await edicion.abrirPanel(id, productos);
      toast.success("producto actualizado");
    } catch (error) {
      toast.error("error al actualizar");
    }
  }

  async function manejarBorrarProducto(id) {
    if (!window.confirm("estas seguro de eliminar este producto?")) return;
    try {
      await borrarProducto(id);
      if (edicion.idProductoEditado === id) {
        edicion.cerrarPanel();
      }
      toast.success("producto eliminado");
    } catch (error) {
      toast.error(error.response?.data?.error || "error al eliminar");
    }
  }

  async function manejarMarcarPrincipal(imagenId) {
    await edicion.marcarPrincipal(imagenId, edicion.idProductoEditado, productos);
    toast.success("portada establecida");
  }

  async function manejarBorrarFoto(imagenId) {
    try {
      await edicion.borrarFoto(imagenId);
      toast.success("imagen eliminada");
    } catch (error) {
      toast.error("error al eliminar foto");
    }
  }

  const productoActualObj = productos.find((p) => p.id === Number(edicion.idProductoEditado));

  return (
    <div className="min-h-screen bg-paper text-ink font-sans antialiased px-4 sm:px-12 lg:px-16 pt-28 sm:pt-32 pb-24 selection:bg-[#E2E2DF]">
      <div className="max-w-3xl mx-auto flex flex-col gap-10">

        {/* encabezado del dashboard */}
        <header className="border-b border-border pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1.5 font-sans">
              <span className="w-1.5 h-1.5 bg-brand" />
              <span className="text-[10px] text-muted uppercase tracking-widest">Panel de Gestión</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-light tracking-tight text-ink">
              CONTROL DE <span className="font-semibold text-brand">INVENTARIO</span>
            </h1>
          </div>
          <div className="font-sans text-[11px] text-muted uppercase self-start sm:self-auto bg-[#E2E2DF]/50 px-3 py-1.5 border border-border rounded">
            Activos: <span className="text-ink font-semibold">{productos.length}</span>
          </div>
        </header>

        {/* seccion para crear un producto nuevo */}
        <section className="border border-border rounded p-6 sm:p-8 bg-white/40 backdrop-blur-sm relative shadow-sm overflow-hidden">
          <div className="absolute top-0 right-0 w-2 h-2 bg-brand" />
          <div className="flex items-center gap-2 mb-6 font-sans">
            <span className="w-1.5 h-1.5 bg-brand" />
            <h2 className="text-xs uppercase tracking-widest text-ink font-semibold">
              01 / Nuevo Artículo
            </h2>
          </div>
          <CrearNuevoProdct
            nuevoProducto={nuevoProducto}
            setNuevoProducto={setNuevoProducto}
            setFotosSeleccionadas={fotos.setFotosSeleccionadas}
            crearNuevoProducto={manejarCrearProducto}
            creando={creando}
          />
        </section>

        {/* seccion para modificar o eliminar un producto */}
        <section className="border border-border rounded p-6 sm:p-8 bg-white/40 backdrop-blur-sm flex flex-col gap-6 relative shadow-sm">
          <div className="absolute top-0 right-0 w-2 h-2 bg-brand rounded-tr" />
          <div className="flex items-center gap-2 font-sans">
            <span className="w-1.5 h-1.5 bg-brand" />
            <h2 className="text-xs uppercase tracking-widest text-ink font-semibold">
              02 / Modificar o Eliminar Artículo
            </h2>
          </div>

          <ProductSelector
            productos={productos}
            productoActual={productoActualObj}
            onSeleccionar={manejarSeleccionarProducto}
            onEliminar={manejarBorrarProducto}
          />

          {edicion.idProductoEditado && productoActualObj && (
            <EditarProductoAdmin
              actualizarDatos={edicion.actualizarDatos}
              setActualizarDatos={edicion.setActualizarDatos}
              imagenesEditor={edicion.imagenesEditor}
              setFotosSeleccionadas={fotos.setFotosSeleccionadas}
              actualizarProducto={manejarActualizarProducto}
              marcarPrincipal={manejarMarcarPrincipal}
              inputClass={inputClass}
              idProductoEditado={edicion.idProductoEditado}
              item={productoActualObj}
              setIdProductoEditado={edicion.cerrarPanel}
              borrarUnaFoto={manejarBorrarFoto}
              previewsPendientes={fotos.previewsPendientes}
            />
          )}
        </section>

      </div>
    </div>
  );
}

export default Dashboard;
